import { isDevelopment } from "@/common/services/chat"

/**
 * @fileoverview Chat Registry for WebSocket Routing
 *
 * **Purpose**: O(1) routing system that maps chatIds to their owning Redux store.
 *
 * **Architecture Decision**:
 * - Registry lives in middleware closure (NOT Redux state)
 * - Why? Components don't need to read it, only middleware uses it for routing
 * - Map provides O(1) lookup performance (critical for 100+ tokens/sec streaming)
 *
 * **Usage Pattern**:
 * ```typescript
 * // Register on chat open
 * on('playgroundChat/openChat', { chatId }) => {
 *   registerChat(chatId, 'playground')
 * }
 *
 * // Route on WebSocket event
 * on('message:streaming', { chatId, token }) => {
 *   const targetStore = getStoreType(chatId)  // O(1) lookup!
 *   dispatch(ACTION_MAP.streaming[targetStore], { chatId, token })
 * }
 * ```
 *
 * **Performance**: O(1) Map lookup vs O(n) state tree traversal
 * **Scalability**: Works for Inbox with 100+ simultaneous chats
 *
 * @module websocket/chatRegistry
 * @see ./index.ts — the router architecture overview this module is part of
 */

/**
 * Store type discriminator
 * Identifies which Redux store owns a particular chat
 */
export type StoreType = "playground" | "embed" | "inbox"

/**
 * Chat registry - maps chatId → storeType
 * Lives in middleware closure for maximum performance
 */
const chatRegistry = new Map<string, StoreType>()

/**
 * Register a chat with its owning store
 *
 * **When to call**: When a chat is opened in any store
 * **Example**:
 * ```typescript
 * // In middleware, intercept openChat action
 * on('playgroundChat/openChat', { chatId }) => {
 *   registerChat(chatId, 'playground')
 *   socket.emit('chat:room:join', { chatId })
 * }
 * ```
 *
 * @param chatId - Unique chat identifier (UUID)
 * @param storeType - Which store owns this chat
 * @returns void
 */
export function registerChat(chatId: string, storeType: StoreType): void {
  chatRegistry.set(chatId, storeType)

  // Debug logging (remove in production)
  if (isDevelopment()) {
    console.log(`[ChatRegistry] Registered: ${chatId} → ${storeType}`)
  }
}

/**
 * Get the store type for a given chatId
 *
 * **Performance**: O(1) Map lookup
 * **Returns**: Store type or undefined if chat not registered
 *
 * @param chatId - Chat identifier to lookup
 * @returns Store type or undefined
 */
export function getStoreType(chatId: string): StoreType | undefined {
  return chatRegistry.get(chatId)
}

/**
 * Unregister a chat (cleanup on deletion)
 *
 * **Important**: Only call on chat DELETION, not on close!
 * We keep chats registered even when inactive to support unread messages.
 *
 * **Example**:
 * ```typescript
 * on('playgroundChat/deleteChat', { chatId }) => {
 *   unregisterChat(chatId)
 *   socket.emit('chat:room:leave', { chatId })
 * }
 * ```
 *
 * @param chatId - Chat identifier to remove
 * @returns boolean - true if chat was registered, false otherwise
 */
export function unregisterChat(chatId: string): boolean {
  const existed = chatRegistry.has(chatId)

  if (existed) {
    chatRegistry.delete(chatId)

    if (isDevelopment()) {
      console.log(`[ChatRegistry] Unregistered: ${chatId}`)
    }
  }

  return existed
}

/**
 * Check if a chat is registered
 *
 * @param chatId - Chat identifier to check
 * @returns boolean - true if registered
 */
export function isChatRegistered(chatId: string): boolean {
  return chatRegistry.has(chatId)
}

/**
 * Get registry size (for debugging/monitoring)
 *
 * @returns number - Count of registered chats
 */
export function getRegistrySize(): number {
  return chatRegistry.size
}

/**
 * Get all registered chats (for debugging)
 *
 * **Warning**: Only use for debugging! Don't use in production code.
 *
 * @returns Array of [chatId, storeType] tuples
 */
export function getAllRegisteredChats(): Array<[string, StoreType]> {
  return Array.from(chatRegistry.entries())
}

/**
 * Clear the entire registry (for testing only)
 *
 * **Warning**: Only call in test teardown! Never in production.
 */
export function clearRegistry(): void {
  // Check if we're in a test environment (vitest/jest)
  // Use 'in' operator to safely check for test globals
  const isTestEnv =
    typeof globalThis !== "undefined" && ("vitest" in globalThis || "jest" in globalThis)

  if (!isTestEnv) {
    console.warn("[ChatRegistry] clearRegistry called outside of tests!")
  }
  chatRegistry.clear()
}
